import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useDropzone } from "react-dropzone";
import { useForm, Controller } from "react-hook-form";
import { TextField, Checkbox  } from '@mui/material';
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import ModalWrapperChildren from '@/views/components/Modal/ModalWrapperChildren';

import api from "@/services/api";

const ModalUploadHeader = ({reloadDepartments, itemToEdit, setItemToEdit, showModalHeaderFooter, setShowModalHeaderFooter}) => {
	const { dict } = useSelector(state => state.app);
	const [ requestIsPending, setRequestIsPending ] = useState(false);
	const [ error, setErrorFile ] = useState(null);
	const [ image, setImage ] = useState('');
	const [ newImage, setNewImage ] = useState(null);
	const [ noImage, setNoImage ] = useState(null);
	const [ oldFile, setOldFile ] = useState(null);

	const { titleHeader, titleFooter, descHeader, descFooter, descHeaderPart2, descFooterPart2, labelHeader, labelFooter, info, tImageOld, tNewImage, browse, dragFiles, tNoImage } = dict.departments.modalEditHeaderFooter;

	const validationSchema = Yup.object({
		text: Yup.string().required(dict.departments.modalEditHeaderFooter.fieldRequired),
	});
	
	const { handleSubmit, control, setValue, formState: { errors, isValid } } = useForm({
		resolver: yupResolver(validationSchema),
		defaultValues: {text: ''},
		reValidateMode: 'onChange',
		mode: 'onChange',
	});
	
	const resetFile = () => {
		setNewImage(null);
	};

	const { acceptedFiles, fileRejections, getRootProps, getInputProps  } = useDropzone({
		accept: {
			"image/*": [ ".jpg", ".png", ".bmp" ],
		},
		multiple: false,
		onFileDialogCancel: () => resetFile(),
	});

	const getUrlApi = () => {
		return showModalHeaderFooter.header ? '/letter-templates/headers' : '/letter-templates/footers';
	};

	const changeNoImage = (e) => {
		setNoImage(e.target.checked);
	};

	const sendHeaderFooter = async (data) => {
		setRequestIsPending(true);
		const formData = new FormData();
		const toSend = {
			department: {
				id: itemToEdit.id,
			},
			text: data.text,
		};
			
		formData.append("json", JSON.stringify(toSend));
		
		if (!noImage && (acceptedFiles.length > 0 || oldFile)) {
			try {
				if(acceptedFiles.length > 0) {
					formData.append("image", acceptedFiles[0]);
				} else if (oldFile) {
					formData.append("image", oldFile);
				}

				const apiUrl = getUrlApi();

				await api.post(apiUrl, formData, {
					headers: {
						"Content-Type": "multipart/form-data",
					},
				});
				setShowModalHeaderFooter({show: false});
				reloadDepartments();
			} catch (error) {
				// eslint-disable-next-line no-console
				console.log(error);
			} finally {
				setRequestIsPending(false);
			}	
		} else {
			try {
				await api.post(`${getUrlApi()}/without-image`,formData,  {
					headers: {
						'Content-Type': 'multipart/form-data',
					},
				});
				setRequestIsPending(false);
				setShowModalHeaderFooter({show: false});
				reloadDepartments();

			} catch (err) {
				// eslint-disable-next-line no-console
				console.log(error);
			} finally {
				setRequestIsPending(false);
			}
	
		}
	};

	const getFooterHeader = async () => {
		const apiUrl = getUrlApi(); 

		const id = showModalHeaderFooter.header ? `${itemToEdit.headerId}` : `${itemToEdit.footerId}`;
		
		const res = await api.get(`${apiUrl}/${id}`);
		
		setValue('text', res.data.text, {shouldValidate:true});

		if(res.data.filename !== "empty.png") {
			const resImage = await api.get(`${apiUrl}/${id}/image`, { responseType: 'arraybuffer' });

			if(resImage.data) {
				const blob = new Blob([ resImage.data ]);
				const file = new File([ blob ], res.data.filename);
	
				var objectURL = URL.createObjectURL(blob);
				setOldFile(file);
				setImage(objectURL);
	
				return () => URL.revokeObjectURL(blob);
			}
		}
	};

	const getImageBase64 = (file) => {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = () => setNewImage(reader.result);
		reader.onerror = (err) => {
			// eslint-disable-next-line no-console
			console.log('Error:', err);
		};
	};

	const files = acceptedFiles && acceptedFiles.map(file => {
		getImageBase64(file);
		return (
			<p className="modal__load-dictionary--file"
				key={ file.name }>
				{dict.dictionary.modalLoad.file} <strong>{file.name}</strong>
			</p>
		);
	});

	const getError = err => {
		switch (err) {
			case "file-invalid-type":
				return dict.departments.modalEditHeaderFooter.errors.format;
			case "too-many-files":
				return dict.departments.modalEditHeaderFooter.errors.files;
			default:
				return "";
		}
	};

	useEffect(() => {
		if(fileRejections.length > 0) {
			const err = fileRejections.map(f => f.errors.map(e => getError(e.code)));
			setErrorFile(err[0]);
		} else {
			setErrorFile('');	
		}
	}, [ fileRejections ]);

	useEffect(() => {
		getFooterHeader();
	}, [ itemToEdit ]);

	return (
		
		<ModalWrapperChildren 
			onClose={ () => { setShowModalHeaderFooter({show: false}); setItemToEdit(null); } }
			modalVisible={ showModalHeaderFooter.show }
			onConfirm={ handleSubmit(sendHeaderFooter) }
			closeButtonName={ dict.cancel }
			confirmButtonName={ dict.saveChanges }
			requestIsPending={ requestIsPending }
			disabled={ !isValid }
			title={ showModalHeaderFooter.header ? titleHeader : titleFooter }
			description={ <span>
				{showModalHeaderFooter.header ? descHeader : descFooter } <strong>{itemToEdit.name}</strong>. {showModalHeaderFooter.header ? descHeaderPart2 : descFooterPart2 }
			</span> }
			isLine
		>		
			<div>
				<form >
					<label 
						className="modal-children__label"
						htmlFor='text'>
						{showModalHeaderFooter.header ? labelHeader : labelFooter}
					</label>
					<Controller
						render={ ({ field }) => <TextField 
							className="modal-children__textarea"
							size='small'
							multiline
							fullWidth
							placeholder={ showModalHeaderFooter.header ? labelHeader : labelFooter }
							inputProps={ { maxLength: 4096 } }
							{ ...field } 
						/> }
						name='text'
						control={ control }
					/>
					<p className="modal-children__error">{errors && errors.text && errors.text.message}</p>
				</form>
				<p className="modal-children__info">{info}</p>
				<section className="modal__load-dictionary--dropzone">
					<div { ...getRootProps({ className: "addletter__dropzone" }) }>
						<input { ...getInputProps() } />
						<p><span>{browse}</span> {dragFiles}</p>
						{files && files}
						{error && <p className="modal__load-dictionary--error">
							{error}
						</p>}
					</div>
				</section>
				{image && 
				<div className="modal-children__no-image">		
					<Checkbox
						id="noImage"
						checked={ noImage }
						color='primary'
						onClick={ (e)=> changeNoImage(e) }
					/>	
					<label htmlFor='noImage'
						className="modal-children__label">{tNoImage}</label>
				</div>}
				{!noImage && !newImage && image && <div>
					<p  className="modal-children__label">{tImageOld}</p>
					<img src={ image }
						className="modal-children__image"/>
				</div>}
				{!noImage && newImage && <div>
					<p  className="modal-children__label">{tNewImage}</p>
					<img src={ newImage }
						className="modal-children__image"/>
				</div>}
			</div>
		</ModalWrapperChildren>
	);
};

export default ModalUploadHeader;
